import React, { FC } from 'react'
import { IntlProvider } from 'react-intl'
import { ThemeProvider } from 'styled-components'

import { theme } from '@elamajs/ui-kit'

import { Chart, Tooltip } from '../Visualisation/components'

import {
  CPMChart,
  CoverageChart,
  CPVChart,
  CostChart,
  ERChart,
  BudgetAndQuantityChart
} from '../AnalyticsNative'
import { useData } from './hooks'
import { getAverageData } from '../Visualisation/helpers'
import { useMediaQuery } from '../Visualisation/hooks'
import Filters from './Filters'

import * as Styled from '../Visualisation/Visualisation.styled'
import * as StyledAnalyticsNative from '../AnalyticsNative/AnalyticsNative.styled'

const MEDIUM = 172
const LAST = 330
const LARGE = 373

const MaxStat: FC = () => {
  const { data, fetchData, isFetching } = useData()
  const displayType = useMediaQuery()

  return (
    <>
      <StyledAnalyticsNative.Title>Аналитика MAX по&nbsp;тематикам</StyledAnalyticsNative.Title>
      <Styled.Main data-sticky-container>
        <Filters data={data} isFetching={isFetching} fetchData={fetchData} />
        <Styled.Body>
          <Styled.Row $raiseTitleOnMobile>
            <Styled.Title>
              Стоимость 1000 показов (CPM) в&nbsp;Max
              <Styled.TooltipTitle
                as={Tooltip}
                direction={displayType === 'mobile' ? 'bottom-start' : 'top-start'}
              >
                <span>
                  CPM = сумма затрат на&nbsp;все размещения &divide; суммарное количество показов
                  (подписчики &times; ER) &times; 1000
                </span>
              </Styled.TooltipTitle>
            </Styled.Title>
            <Styled.FlexContainer>
              <Chart
                graphHeight={MEDIUM}
                data={data?.data.cpm}
                withAverage
                averageCurrencyType="RUB"
                averageHiddenOnMobile={false}
                calculateAverage={data => Math.round(getAverageData(data) ?? 0)}
              >
                <CPMChart toFixed={0} data={data?.data.cpm} graphHeight={MEDIUM} />
              </Chart>
            </Styled.FlexContainer>
          </Styled.Row>
          <Styled.Row>
            <Styled.Title>Охват интеграций и&nbsp;уровень вовлеченности</Styled.Title>
            <Styled.FlexContainer>
              <Chart
                graphHeight={LARGE}
                title={
                  <>
                    Охват на&nbsp;одну публикацию&nbsp;
                    <Styled.Tooltip as={Tooltip} direction="top-start">
                      <span>
                        Средний показатель охвата на&nbsp;одну публикацию в&nbsp;каналах тематики
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                data={data?.data.coverage}
                withAverage
                averageHiddenOnMobile={false}
                calculateAverage={data => Math.round(getAverageData(data) ?? 0)}
              >
                <CoverageChart data={data?.data.coverage} graphHeight={LARGE} />
              </Chart>
              <Chart
                graphHeight={LARGE}
                title={
                  <>
                    ER публикации, %&nbsp;
                    <Styled.Tooltip as={Tooltip} direction="top-start">
                      <span>
                        Средний ER = сумма кликов по&nbsp;всем рекламным публикациям в&nbsp;тематике
                        &divide; сумма подписчиков всех каналов, где они размещались
                        (в&nbsp;процентах)
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                data={data?.data.er}
                withAverage
                averageHiddenOnMobile={false}
              >
                <ERChart data={data?.data.er} graphHeight={LARGE} />
              </Chart>
            </Styled.FlexContainer>
          </Styled.Row>
          <Styled.Row>
            <Styled.Title>
              Стоимость просмотра и&nbsp;средняя цена публикации в&nbsp;рамках тематики
            </Styled.Title>
            <Styled.FlexContainer>
              <Chart
                graphHeight={LARGE}
                data={data?.data.cpv}
                title={
                  <>
                    CPV на&nbsp;одну публикацию&nbsp;
                    <Styled.Tooltip as={Tooltip} direction="top-start">
                      <span>
                        Средний CPV = сумма затрат на&nbsp;все рекламные посты &divide; общее
                        количество просмотров
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                withAverage
                averageCurrencyType="RUB"
                averageHiddenOnMobile={false}
              >
                <CPVChart data={data?.data.cpv} graphHeight={LARGE} />
              </Chart>
              <Chart
                graphHeight={LARGE}
                data={data?.data.cost}
                title={
                  <>
                    Стоимость одной публикации&nbsp;
                    <Styled.Tooltip as={Tooltip} direction="top-start">
                      <span>
                        Средний показатель стоимости одной публикации в&nbsp;каналах тематики
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                withAverage
                averageCurrencyType="RUB"
                calculateAverage={data => Math.round(getAverageData(data) ?? 0)}
                averageHiddenOnMobile={false}
              >
                <CostChart data={data?.data.cost} graphHeight={LARGE} />
              </Chart>
            </Styled.FlexContainer>
          </Styled.Row>
          <Styled.Row>
            <Styled.Title>
              Средний бюджет и&nbsp;количество публикаций на&nbsp;рекламодателя
            </Styled.Title>
            <Chart
              graphHeight={LAST}
              legend={[
                {
                  color: '#C88CF2',
                  title: <>Средний бюджет одного рекламодателя</>
                },
                {
                  color: '#3A64F8',
                  title: <>Среднее количество публикаций одного рекламодателя</>
                }
              ]}
              hasData={!!(data?.data?.budget && data?.data?.quantity)}
            >
              <BudgetAndQuantityChart
                graphHeight={LAST}
                data={
                  data?.data?.budget && data?.data?.quantity
                    ? { budget: data.data.budget, quantity: data.data.quantity }
                    : undefined
                }
              />
            </Chart>
          </Styled.Row>
        </Styled.Body>
      </Styled.Main>
    </>
  )
}

const withProviders = (Component: React.ComponentType) => {
  const NewComponent: React.ComponentType = () => {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider locale="ru">
          <Component />
        </IntlProvider>
      </ThemeProvider>
    )
  }

  return NewComponent
}

export default withProviders(MaxStat)
